import React, { useState, useEffect, useRef } from 'react';
import { _T, C } from '../../../shared/theme';
import { s_PB, s_GB, s_GC } from '../../../shared/styles';
import { saveGameScore } from '../../../shared/utils';
import MultiplayerInviteModal from '../../../modals/MultiplayerInviteModal';
import MultiplayerResults from '../../../modals/MultiplayerResults';

// Cooperative breathing sync game — both players follow the same pacer.
// Score is based on how many complete cycles they did together.
// Works solo (self-practice) or with a partner.

const PHASES = [
  { name: 'in',  dur: 4, label: { nl: 'Adem in', en: 'Breathe in' },  color: '#2A9E82' },
  { name: 'hold',dur: 4, label: { nl: 'Vasthoud', en: 'Hold' },         color: '#5B9ED0' },
  { name: 'out', dur: 6, label: { nl: 'Adem uit', en: 'Breathe out' }, color: '#7B6FD0' },
  { name: 'rest',dur: 2, label: { nl: 'Rust',     en: 'Rest' },         color: '#8899BB' },
];
const TOTAL_CYCLE = PHASES.reduce((s, p) => s + p.dur, 0); // 16 s per cycle
const TOTAL_ROUNDS = 5; // 5 full cycles = 80 seconds of sync breathing

function CoopBreathGame(props) {
  const { lang, onBack, allUsers, friends, currentUserId, userName, invitePartner } = props;
  const nl = lang === 'nl';
  const [partner, setPartner] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [phase, setPhase] = useState(0);
  const [progress, setProgress] = useState(0); // 0-1 within phase
  const [cycle, setCycle] = useState(0);       // completed cycles
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const [showResults, setShowResults] = useState(null);
  const tickRef = useRef(null);
  const elapsedRef = useRef(0); // ms into current cycle

  useEffect(() => {
    if (invitePartner) setPartner(invitePartner);
  }, [invitePartner]); // eslint-disable-line

  useEffect(() => {
    if (props.onRealPartner) props.onRealPartner(null); // voice not needed for coop breath
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!running || done) return;
    tickRef.current = setInterval(() => {
      elapsedRef.current += 100;
      let rem = (elapsedRef.current / 1000) % TOTAL_CYCLE;
      let phaseIdx = 0, acc = 0;
      for (let i = 0; i < PHASES.length; i++) {
        if (rem < acc + PHASES[i].dur) { phaseIdx = i; break; }
        acc += PHASES[i].dur;
      }
      const phaseProg = Math.min((rem - acc) / PHASES[phaseIdx].dur, 1);
      setPhase(phaseIdx);
      setProgress(phaseProg);

      const totalSecs = elapsedRef.current / 1000;
      const completedCycles = Math.floor(totalSecs / TOTAL_CYCLE);
      setCycle(completedCycles);

      if (completedCycles >= TOTAL_ROUNDS) {
        clearInterval(tickRef.current);
        setRunning(false);
        setDone(true);
        const score = TOTAL_ROUNDS * 20; // perfect sync = 100
        saveGameScore('coopbreath', score);
        if (partner) {
          const partnerScore = Math.floor(Math.random() * 15) + 85;
          setShowResults({ myScore: score, partnerScore });
        } else {
          setShowResults({ myScore: score, partnerScore: null });
        }
      }
    }, 100);
    return () => clearInterval(tickRef.current);
  }, [running, done, partner]); // eslint-disable-line

  const start = () => { elapsedRef.current = 0; setPhase(0); setProgress(0); setCycle(0); setDone(false); setShowResults(null); setRunning(true); };

  const cur = PHASES[phase];
  // Circle scale: in → grow, out → shrink, hold/rest → stable
  const scale = phase === 0 ? 0.45 + 0.55 * progress : phase === 1 ? 1.0 : phase === 2 ? 1.0 - 0.55 * progress : 0.45;
  const r = 90 * scale;
  const glowSize = Math.round(scale * 40);

  if (showResults) {
    return (
      <MultiplayerResults
        lang={lang}
        myScore={showResults.myScore}
        myLabel={nl ? 'sync score' : 'sync score'}
        partnerId={partner?.id}
        partnerScore={showResults.partnerScore}
        partnerLabel={nl ? 'sync score' : 'sync score'}
        aiComment={nl ? `Jullie hebben ${TOTAL_ROUNDS} ademcycli samen voltooid. 🌬️` : `You completed ${TOTAL_ROUNDS} breath cycles together. 🌬️`}
        onClose={onBack}
        onRematch={start}
      />
    );
  }

  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: C.silver, cursor: 'pointer', fontSize: 20 }}>←</button>
        <div>
          <div style={{ fontFamily: 'Georgia,serif', fontSize: 18, color: C.white }}>🤝 {nl ? 'Sync Adem' : 'Sync Breath'}</div>
          <div style={{ color: C.silver, fontSize: 11 }}>{nl ? 'Adem samen met je partner' : 'Breathe in sync with your partner'}</div>
        </div>
        {!partner && !running && (
          <button onClick={() => setShowInvite(true)} style={{ background: `${_T.accent}18`, border: `1px solid ${_T.accent}44`, borderRadius: 10, padding: '6px 12px', cursor: 'pointer', color: _T.accent, fontSize: 11 }}>
            {nl ? '+ Partner' : '+ Partner'}
          </button>
        )}
        {partner && <div style={{ fontSize: 12, color: C.teal }}>👤 {partner.name}</div>}
        {!partner && running && <div style={{ width: 60 }} />}
      </div>

      {/* Cycle progress */}
      <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 20 }}>
        {Array.from({ length: TOTAL_ROUNDS }).map((_, i) => (
          <div key={i} style={{ width: 32, height: 6, borderRadius: 3, background: i < cycle ? '#2A9E82' : i === cycle && running ? `${cur.color}88` : _T.bg4 }} />
        ))}
      </div>

      {/* Breathing circle */}
      {running ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ width: 220, height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{
              width: r * 2, height: r * 2, borderRadius: '50%',
              background: `radial-gradient(circle, ${cur.color}30 0%, ${cur.color}08 70%)`,
              border: `2px solid ${cur.color}99`,
              boxShadow: `0 0 ${glowSize}px ${cur.color}44`,
              transition: 'width 0.1s ease, height 0.1s ease',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            }}>
              <div style={{ color: cur.color, fontSize: 13, fontWeight: 700, letterSpacing: 1 }}>
                {nl ? cur.label.nl : cur.label.en}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ marginBottom: 30, color: C.silver, fontSize: 13 }}>
          {done
            ? (nl ? '✅ Alle cycli voltooid!' : '✅ All cycles complete!')
            : (nl ? `${TOTAL_ROUNDS} rustige ademcycli samen.` : `${TOTAL_ROUNDS} calm breath cycles together.`)}
        </div>
      )}

      {!running && (
        <button style={s_PB({ width: '100%', fontSize: 15 })} onClick={start}>
          {done ? (nl ? '🔄 Opnieuw' : '🔄 Again') : (nl ? '▶ Start samen' : '▶ Start together')}
        </button>
      )}
      {running && (
        <button style={s_GB({ width: '100%', fontSize: 14 })} onClick={() => { clearInterval(tickRef.current); setRunning(false); }}>
          ⏸ {nl ? 'Pauzeer' : 'Pause'}
        </button>
      )}

      {showInvite && (
        <MultiplayerInviteModal
          lang={lang}
          title={nl ? 'Sync Adem' : 'Sync Breath'}
          icon="🤝"
          allUsers={allUsers}
          friends={friends}
          onInvite={(u) => { setPartner(u); setShowInvite(false); }}
          onClose={() => setShowInvite(false)}
        />
      )}
    </div>
  );
}

export default CoopBreathGame;
